/**
 * Task page copy: the task's fields and sections, the action labels, the
 * Owner's edit and deletion, and every transition dialog.
 */

export interface TaskDetailCopy {
  readonly task: {
    readonly suggested: (priority: string) => string;
    readonly suggestedNotApproved: string;
    readonly authoritativePriority: string;
    readonly noPriority: string;
    readonly proposedBy: string;
    readonly assignee: string;
    readonly approvedBy: string;
    readonly unassigned: string;
    readonly difficulty: string;
    readonly type: string;
    readonly requiresMerge: string;
    readonly githubReferences: string;
    readonly githubUnverified: string;
    readonly noReferences: string;
    readonly addReference: string;
    readonly referenceKind: string;
    readonly referenceKinds: Readonly<Record<string, string>>;
    readonly referenceUrl: string;
    readonly history: string;
    readonly description: string;
    readonly created: string;
    readonly updated: string;
    readonly notFoundTitle: string;
    readonly notFoundBody: string;
    readonly backToBoard: string;
    readonly submittedDocumentLabel: string;
    readonly submittedRevision: (version: number) => string;
    readonly submittedDocumentDeleted: string;
    readonly submissionNoteHeading: string;
    readonly submissionNoteBy: (name: string) => string;
    readonly ownCompletionNotice: string;
  };

  readonly actions: {
    readonly claim: string;
    readonly submit: string;
    readonly approve: string;
    readonly reject: string;
    readonly complete: string;
    /** The Owner finishing their own assigned work. Never "complete". */
    readonly completeOwn: string;
    readonly returnToProgress: string;
    readonly edit: string;
    readonly cancel: string;
    readonly confirm: string;
    readonly save: string;
    readonly retry: string;
    readonly close: string;
  };

  readonly edit: {
    readonly title: string;
    readonly body: string;
    readonly effects: readonly string[];
    readonly activeWarning: string;
    readonly doneWarning: string;
    readonly titleLabel: string;
    readonly approvedPriorityLabel: string;
    readonly approvedPriorityBacklog: string;
    readonly unassignLabel: string;
    readonly unassignHint: string;
    readonly noChanges: string;
    readonly saved: string;
  };

  readonly deletion: {
    readonly action: string;
    readonly title: string;
    readonly body: (taskTitle: string) => string;
    readonly effects: readonly string[];
    readonly deleted: (reference: string) => string;
  };

  readonly dialogs: {
    readonly whatHappens: string;
    readonly claimTitle: string;
    readonly claimBody: (reference: string) => string;
    readonly claimEffects: readonly string[];
    readonly submitTitle: string;
    readonly submitBody: string;
    readonly submitEffects: readonly string[];
    readonly issueUrl: string;
    readonly pullRequestUrl: string;
    readonly mergeUrl: string;
    readonly mergeUrlOptional: string;
    readonly submitDocumentLabel: string;
    readonly submitDocumentNone: string;
    readonly submitDocumentHint: string;
    readonly submitNoteLabel: string;
    readonly submitNoteHint: string;
    readonly submitEvidenceHint: string;
    readonly completeOwnTitle: string;
    readonly completeOwnBody: string;
    readonly completeOwnEffects: readonly string[];
    readonly completeOwnNoteLabel: string;
    readonly completeOwnNoteHint: string;
    readonly approveTitle: string;
    readonly approveBody: string;
    readonly approveEffects: readonly string[];
    readonly priorityLabel: string;
    readonly priorityDefinitions: Readonly<Record<string, string>>;
    readonly priorityMissing: string;
    readonly approveDescriptionHint: string;
    readonly requiresMergeLabel: string;
    readonly rejectTitle: string;
    readonly rejectBody: string;
    readonly rejectEffects: readonly string[];
    readonly completeTitle: string;
    readonly completeBody: string;
    readonly completeEffects: readonly string[];
    readonly returnTitle: string;
    readonly returnBody: string;
    readonly returnEffects: readonly string[];
    readonly reasonLabel: string;
    readonly reasonRequired: string;
  };
}

export const TASK_DETAIL_COPY_EN: TaskDetailCopy = {
  task: {
    suggested: (priority) => `Suggested ${priority}`,
    suggestedNotApproved: 'not approved',
    authoritativePriority: 'Authoritative priority',
    noPriority: 'No priority yet',
    proposedBy: 'Proposed by',
    assignee: 'Assignee',
    approvedBy: 'Approved by',
    unassigned: 'Unassigned',
    difficulty: 'Difficulty',
    type: 'Type',
    requiresMerge: 'A successful merge is expected',
    githubReferences: 'GitHub references',
    githubUnverified:
      'Entered by the contributor. Nothing here is verified by an integration.',
    noReferences: 'No references have been entered.',
    addReference: 'Add a reference',
    referenceKind: 'Reference type',
    referenceKinds: { ISSUE: 'Issue', PULL_REQUEST: 'Pull request', MERGE: 'Merge' },
    referenceUrl: 'GitHub URL',
    history: 'History',
    description: 'Description',
    created: 'Created',
    updated: 'Updated',
    notFoundTitle: 'This page is not available',
    notFoundBody:
      'The page does not exist, or your role does not include it. If you followed a link from a teammate, ask them to check it.',
    backToBoard: 'Back to the task board',
    submittedDocumentLabel: 'Submitted document',
    submittedRevision: (version) => `as submitted — version ${String(version)}`,
    submittedDocumentDeleted:
      'The document has since been deleted; the submitted version remains on record.',
    submissionNoteHeading: 'Submission note',
    submissionNoteBy: (name) => `from ${name}`,
    ownCompletionNotice:
      'The owner completed this task themselves; it was not reviewed by anybody else.',
  },

  actions: {
    claim: 'Claim into In progress',
    submit: 'Submit for review',
    approve: 'Approve into Ready',
    reject: 'Reject',
    complete: 'Approve as done',
    completeOwn: 'Mark as done',
    returnToProgress: 'Return to In progress',
    edit: 'Edit task',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    retry: 'Try again',
    close: 'Close',
  },

  edit: {
    title: 'Edit this task',
    body: 'Change what the task is. Where it stands, who has it, and everything already recorded stay exactly as they are.',
    effects: [
      'The task keeps its state, its assignee, and its history.',
      'The edit is recorded with your name and a before-and-after summary.',
      'Anyone reading the task sees the new version immediately.',
    ],
    activeWarning:
      'Somebody is working on this task right now. Changing its shape mid-flight can invalidate work already done — tell them what changed.',
    doneWarning:
      'This task is finished. Editing it changes the record of completed work; the contribution already recorded is not rewritten.',
    titleLabel: 'Title',
    approvedPriorityLabel: 'Approved priority',
    approvedPriorityBacklog:
      'A proposal has no approved priority until an Owner approves it.',
    unassignLabel: 'Unassign the current assignee',
    unassignHint:
      'This task type would take the task away from the person assigned to it. Confirm to unassign them, or choose a type they can still work on.',
    noChanges: 'Nothing changed.',
    saved: 'Task updated.',
  },

  deletion: {
    action: 'Delete task',
    title: 'Delete this task',
    body: (taskTitle) =>
      `"${taskTitle}" will be removed from the board, the counts, and the feeds, for everyone.`,
    effects: [
      'The task disappears everywhere and cannot be opened, claimed, or edited.',
      'Its history, contributions, references, and dependencies are preserved in the record.',
      'The deletion is recorded with your name and the time.',
      'There is no undo in the workspace.',
    ],
    deleted: (reference) => `${reference} deleted.`,
  },

  dialogs: {
    whatHappens: 'What happens',
    claimTitle: 'Claim this task',
    claimBody: (reference) => `${reference} will be assigned to you.`,
    claimEffects: [
      'The task moves to In progress with your name on it.',
      'It appears under My active task on your dashboard.',
      'Only you can submit it for review.',
    ],
    submitTitle: 'Submit for review',
    submitBody:
      'A peer will review this work. Tell them what you did — and attach whatever evidence exists, if any.',
    submitEffects: [
      'The task moves to In review.',
      'You remain the assignee.',
      'Any contributor with the capability — except you — can review it.',
      'Your note is recorded with this submission and kept, even if the task comes back.',
    ],
    issueUrl: 'Issue URL',
    pullRequestUrl: 'Pull request URL',
    mergeUrl: 'Merge reference',
    mergeUrlOptional: 'Add it when the merge has happened.',
    submitDocumentLabel: 'Data Room document',
    submitDocumentNone: 'No document',
    submitDocumentHint:
      'Research and decision work can present a Data Room document as the submission. The exact version you submit is pinned for the reviewer.',
    submitNoteLabel: 'Submission note',
    submitNoteHint:
      'Optional. Summarise what you completed, the evidence behind it, anything you left out, and what the reviewer should look at first. Markdown is supported.',
    submitEvidenceHint:
      'Every link below is optional. Attach what exists; a merge link can be added later when the merge happens.',
    completeOwnTitle: 'Mark your own task as done',
    completeOwnBody:
      'You are the assignee, so nobody else reviews this. Completing it yourself is recorded as exactly that.',
    completeOwnEffects: [
      'The task moves to Done and appears in contributions like any other.',
      'The history records that you completed your own assigned work — not that it was reviewed.',
      'Your note, if you write one, is kept with that record.',
    ],
    completeOwnNoteLabel: 'Completion note',
    completeOwnNoteHint:
      'Optional. What was finished, and anything the next person should know. Markdown is supported.',
    approveTitle: 'Approve into Ready',
    approveBody:
      'Approval sets the authoritative priority. You can confirm or adjust the type, difficulty and description before it becomes claimable.',
    approveEffects: [
      'The task moves to Ready and becomes claimable.',
      'The priority you set here is the one the team acts on.',
      'The proposer’s suggestion stays recorded as a suggestion.',
    ],
    priorityLabel: 'Authoritative priority',
    priorityDefinitions: {
      P1: 'Drop other work. Blocking the product or the team.',
      P2: 'Next in line. Important for the current milestone.',
      P3: 'Normal flow. Scheduled with everything else.',
      P4: 'When there is room. Nice to have.',
    },
    priorityMissing: 'Choose the authoritative priority before approving.',
    approveDescriptionHint:
      'Confirm or edit the description. Append acceptance criteria as a list — they are recorded with it as one Markdown record.',
    requiresMergeLabel: 'A successful merge is expected for this work',
    rejectTitle: 'Reject this task',
    rejectBody: 'The reason you give is recorded and shown to the proposer.',
    rejectEffects: [
      'The task moves to Rejected and cannot be claimed.',
      'The reason becomes part of the permanent history.',
    ],
    completeTitle: 'Approve as done',
    completeBody: 'You are confirming this work meets its acceptance criteria.',
    completeEffects: [
      'The task moves to Done.',
      'Your name and the time are recorded as the reviewer.',
    ],
    returnTitle: 'Return to In progress',
    returnBody: 'Tell the assignee what needs to change. They keep the task.',
    returnEffects: [
      'The task moves back to In progress.',
      'The assignee is unchanged.',
      'Your reason is recorded in the history.',
    ],
    reasonLabel: 'Reason',
    reasonRequired: 'A reason is required.',
  },
};

export const TASK_DETAIL_COPY_AR: TaskDetailCopy = {
  task: {
    suggested: (priority) => `أولوية مقترحة: ${priority}`,
    suggestedNotApproved: 'غير معتمدة',
    authoritativePriority: 'الأولوية المعتمدة',
    noPriority: 'بلا أولوية بعد',
    proposedBy: 'اقترحها',
    assignee: 'المكلَّف',
    approvedBy: 'اعتمدها',
    unassigned: 'غير مُسندة',
    difficulty: 'الصعوبة',
    type: 'النوع',
    requiresMerge: 'يُتوقَّع دمج ناجح',
    githubReferences: 'مراجع GitHub',
    githubUnverified: 'أدخلها المساهم. لا يتحقق منها أي تكامل.',
    noReferences: 'لم تُدخل أي مراجع.',
    addReference: 'إضافة مرجع',
    referenceKind: 'نوع المرجع',
    referenceKinds: { ISSUE: 'مشكلة', PULL_REQUEST: 'طلب سحب', MERGE: 'دمج' },
    referenceUrl: 'رابط GitHub',
    history: 'السجل',
    description: 'الوصف',
    created: 'أُنشئت',
    updated: 'آخر تحديث',
    notFoundTitle: 'هذه الصفحة غير متاحة',
    notFoundBody:
      'الصفحة غير موجودة، أو أن دورك لا يشملها. إذا وصلت عبر رابط من زميل فاطلب منه التحقق منه.',
    backToBoard: 'العودة إلى لوحة المهام',
    submittedDocumentLabel: 'المستند المُقدَّم',
    submittedRevision: (version) => `كما قُدِّم — الإصدار ${String(version)}`,
    submittedDocumentDeleted:
      'حُذفت الوثيقة لاحقًا؛ ويبقى الإصدار المُقدَّم محفوظًا في السجل.',
    submissionNoteHeading: 'ملاحظة الإرسال',
    submissionNoteBy: (name) => `من ${name}`,
    ownCompletionNotice: 'أنجز المالك هذه المهمة بنفسه؛ ولم يراجعها أحد سواه.',
  },

  actions: {
    claim: 'استلام إلى قيد التنفيذ',
    submit: 'إرسال للمراجعة',
    approve: 'اعتماد إلى جاهزة',
    reject: 'رفض',
    complete: 'اعتماد كمنجزة',
    completeOwn: 'وضع علامة منجزة',
    returnToProgress: 'إعادة إلى قيد التنفيذ',
    edit: 'تعديل المهمة',
    cancel: 'إلغاء',
    confirm: 'تأكيد',
    save: 'حفظ',
    retry: 'إعادة المحاولة',
    close: 'إغلاق',
  },

  edit: {
    title: 'تعديل هذه المهمة',
    body: 'غيّر ما هي المهمة. أما موضعها ومن يحملها وكل ما سُجّل من قبل فيبقى كما هو.',
    effects: [
      'تحتفظ المهمة بحالتها والمكلَّف بها وسجلها.',
      'يُسجَّل التعديل باسمك مع ملخّص لما كان وما صار.',
      'يرى كل من يقرأ المهمة النسخة الجديدة فورًا.',
    ],
    activeWarning:
      'هناك من يعمل على هذه المهمة الآن. تغيير شكلها أثناء التنفيذ قد يُبطل عملًا أُنجز — أخبره بما تغيّر.',
    doneWarning:
      'هذه المهمة منجزة. تعديلها يغيّر سجل عمل مكتمل؛ ولا تُعاد كتابة المساهمة المسجَّلة.',
    titleLabel: 'العنوان',
    approvedPriorityLabel: 'الأولوية المعتمدة',
    approvedPriorityBacklog: 'لا أولوية معتمدة للمقترح حتى يعتمده المالك.',
    unassignLabel: 'إلغاء إسناد المهمة عن المكلَّف الحالي',
    unassignHint:
      'نوع المهمة هذا سيحجبها عن الشخص المكلَّف بها. أكّد إلغاء الإسناد، أو اختر نوعًا يستطيع العمل عليه.',
    noChanges: 'لم يتغيّر شيء.',
    saved: 'حُدِّثت المهمة.',
  },

  deletion: {
    action: 'حذف المهمة',
    title: 'حذف هذه المهمة',
    body: (taskTitle) => `ستُزال «${taskTitle}» من اللوحة والأعداد والسجلات، للجميع.`,
    effects: [
      'تختفي المهمة من كل مكان ولا يمكن فتحها أو استلامها أو تعديلها.',
      'يبقى سجلها ومساهماتها ومراجعها ومتطلباتها محفوظة في السجل.',
      'يُسجَّل الحذف باسمك ووقته.',
      'لا تراجع عن الحذف داخل مساحة العمل.',
    ],
    deleted: (reference) => `حُذفت ${reference}.`,
  },

  dialogs: {
    whatHappens: 'ماذا سيحدث',
    claimTitle: 'استلام هذه المهمة',
    claimBody: (reference) => `ستُسند ${reference} إليك.`,
    claimEffects: [
      'تنتقل المهمة إلى «قيد التنفيذ» وباسمك.',
      'تظهر ضمن «مهمتي النشطة» في لوحة عملك.',
      'أنت وحدك من يستطيع إرسالها للمراجعة.',
    ],
    submitTitle: 'إرسال للمراجعة',
    submitBody:
      'سيراجع زميل هذا العمل. أخبره بما أنجزت — وأرفق ما وُجد من أدلة، إن وُجد.',
    submitEffects: [
      'تنتقل المهمة إلى «قيد المراجعة».',
      'تبقى أنت المكلَّف بها.',
      'يمكن لأي مساهم يملك الصلاحية — غيرك — مراجعتها.',
      'تُسجَّل ملاحظتك مع هذا الإرسال وتبقى، حتى لو أُعيدت المهمة.',
    ],
    issueUrl: 'رابط المشكلة',
    pullRequestUrl: 'رابط طلب السحب',
    mergeUrl: 'مرجع الدمج',
    mergeUrlOptional: 'أضفه بعد حدوث الدمج.',
    submitDocumentLabel: 'مستند من غرفة البيانات',
    submitDocumentNone: 'بدون مستند',
    submitDocumentHint:
      'أعمال البحث والقرارات يمكن أن تُقدَّم كمستند من غرفة البيانات. يُثبَّت الإصدار الذي تقدّمه للمراجع.',
    submitNoteLabel: 'ملاحظة الإرسال',
    submitNoteHint:
      'اختيارية. لخّص ما أنجزته والأدلة عليه وما تركته، وما ينبغي أن يبدأ به المراجع. تدعم Markdown.',
    submitEvidenceHint:
      'كل رابط أدناه اختياري. أرفق ما هو موجود؛ ويمكن إضافة رابط الدمج لاحقًا عند حدوثه.',
    completeOwnTitle: 'وضع علامة منجزة على مهمتك',
    completeOwnBody:
      'أنت المكلَّف بها، فلا يراجعها أحد سواك. وإنجازك لها بنفسك يُسجَّل بهذا الوصف تمامًا.',
    completeOwnEffects: [
      'تنتقل المهمة إلى «منجزة» وتظهر في المساهمات كغيرها.',
      'يُسجِّل السجل أنك أنجزت عملك المكلَّف به — لا أنه رُوجع.',
      'تُحفظ ملاحظتك، إن كتبتها، مع ذلك السجل.',
    ],
    completeOwnNoteLabel: 'ملاحظة الإنجاز',
    completeOwnNoteHint:
      'اختيارية. ما الذي اكتمل، وما ينبغي أن يعرفه من يأتي بعدك. تدعم Markdown.',
    approveTitle: 'اعتماد إلى جاهزة',
    approveBody:
      'الاعتماد يحدد الأولوية المعتمدة. يمكنك تأكيد النوع والصعوبة والوصف أو تعديلها قبل أن تصبح قابلة للاستلام.',
    approveEffects: [
      'تنتقل المهمة إلى «جاهزة» وتصبح قابلة للاستلام.',
      'الأولوية التي تحددها هنا هي التي يعمل بها الفريق.',
      'يبقى اقتراح صاحب المهمة مسجلًا كاقتراح.',
    ],
    priorityLabel: 'الأولوية المعتمدة',
    priorityDefinitions: {
      P1: 'اترك ما عداها. تعطّل المنتج أو الفريق.',
      P2: 'التالية في الدور. مهمة للمرحلة الحالية.',
      P3: 'السير الطبيعي. تُجدول مع غيرها.',
      P4: 'عند توفر الوقت. تحسين إضافي.',
    },
    priorityMissing: 'اختر الأولوية المعتمدة قبل الاعتماد.',
    approveDescriptionHint:
      'أكّد الوصف أو عدّله. أضف معايير القبول كقائمة — تُسجَّل معه كسجل Markdown واحد.',
    requiresMergeLabel: 'يُتوقَّع دمج ناجح لهذا العمل',
    rejectTitle: 'رفض هذه المهمة',
    rejectBody: 'السبب الذي تكتبه يُسجَّل ويظهر لصاحب الاقتراح.',
    rejectEffects: [
      'تنتقل المهمة إلى «مرفوضة» ولا يمكن استلامها.',
      'يصبح السبب جزءًا من السجل الدائم.',
    ],
    completeTitle: 'اعتماد كمنجزة',
    completeBody: 'أنت تؤكد أن هذا العمل يحقق معايير قبوله.',
    completeEffects: [
      'تنتقل المهمة إلى «منجزة».',
      'يُسجَّل اسمك ووقت المراجعة بصفتك المراجع.',
    ],
    returnTitle: 'إعادة إلى قيد التنفيذ',
    returnBody: 'أخبر المكلَّف بما يلزم تغييره. تبقى المهمة معه.',
    returnEffects: [
      'تعود المهمة إلى «قيد التنفيذ».',
      'يبقى المكلَّف كما هو.',
      'يُسجَّل سببك في السجل.',
    ],
    reasonLabel: 'السبب',
    reasonRequired: 'السبب مطلوب.',
  },
};
